import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { saveAs } from 'file-saver'

import { Button, Text, Separator } from '@/ui'
import { getUserById } from '@/selectors/user.selectors'
import { BalanceSheetBreadcrumbs } from './components/BalanceSheetBreadcrumbs'
import { useReselector } from '@/utils/sharedHooks'
import { adminFetchSingleUser } from '@/actions/admin/adminAllUsersActions'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TypographyStyles } from '@/styles'
import {
  checkAdminBalanceSheet,
  CHECK_BALANCE_SHEET_KEY,
} from '@/actions/reportActions'
import { getIsFetching } from '@/reducers/fetch'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { fetchBalanceSheet } from '@/features/reports/reports.slice'
import { logHeardError } from '@/utils/errorHelpers'

export const AdminAutoBalanceSheetPanel = () => {
  const params = useParams<{ userId: string }>()
  const user = useReselector(getUserById, Number(params.userId))
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const [validationStatus, setValidationStatus] = useState<boolean>()

  const dispatch = useAppDispatch()

  const isFetchingStatus = useReselector(getIsFetching, CHECK_BALANCE_SHEET_KEY)

  useEffect(() => {
    if (params.userId) {
      const userId = Number(params.userId)
      dispatch(adminFetchSingleUser(userId))
    }
  }, [dispatch, params.userId])

  const checkBalanceSheetValidation = async () => {
    if (!user?.id || !currentTaxYear) return
    const response = await checkAdminBalanceSheet(
      user?.id,
      currentTaxYear
    )(dispatch)
    if (response) {
      setValidationStatus(response.validationStatus)
    }
  }

  const downloadBalanceSheet = useCallback(async () => {
    if (!user?.id || !currentTaxYear) return

    try {
      const result = await fetchBalanceSheet(user?.id, currentTaxYear)
      const blob = new Blob([result.data], { type: 'application/pdf' })
      const pdfName = `${currentTaxYear}_balance_sheet.pdf`
      saveAs(blob, pdfName)
    } catch (e) {
      logHeardError(e)
    }
  }, [currentTaxYear, user?.id])

  return (
    <div id="balanceSheetReport">
      <BalanceSheetBreadcrumbs
        firstName={user?.firstName}
        lastName={user?.lastName}
        userId={user?.id}
      />
      <Separator />
      <div className="flex flex-col gap-2 pt-4">
        <Text variant={TypographyStyles.H2} className="w-full">
          Automated Balance Sheet for {currentTaxYear} Tax Year
        </Text>
        <div className="flex flex-col space-y-2">
          <Button
            onClick={checkBalanceSheetValidation}
            loading={isFetchingStatus}
            className="w-1/3"
          >
            Check Balance Sheet Validation Status
          </Button>
          <Text variant={TypographyStyles.BODY_MD}>
            {validationStatus !== undefined && (
              <Text variant={TypographyStyles.BODY_MD}>
                {validationStatus === true
                  ? 'Balance Sheet has passed the checks'
                  : 'Balance Sheet did not pass the automated checks'}
              </Text>
            )}
          </Text>
        </div>
        {validationStatus === true && (
          <div className="flex flex-col">
            <Button onClick={downloadBalanceSheet}>
              Download Balance Sheet File
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
